import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromLonLat, transform } from 'ol/proj';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import axiosInstance from '../axiosIntance';

interface TransactionsFaoLayerOptions {
  visible: boolean;
  selectedParcelle?: string | null;
  filters?: any;
}

// Red and green dot styles for transactions
const transactionsFaoStyles = {
  green: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: "green" }),
      stroke: new Stroke({
        color: "white",
        width: 2,
      }),
    }),
  }),
  red: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: "red" }),
      stroke: new Stroke({
        color: "white",
        width: 2,
      }),
    }),
  }),
  greenSelected: [
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "green" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 1,
    }),
    new Style({
      image: new CircleStyle({
        radius: 9,
        fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
      }),
    }),
  ],
  redSelected: [
    new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "red" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
      zIndex: 1,
    }),
    new Style({
      image: new CircleStyle({
        radius: 9,
        fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
      }),
    }),
  ],
};

export function createTransactionsFaoLayer(options: TransactionsFaoLayerOptions & { parcelles?: any[] }) {
  const { visible, selectedParcelle, filters, parcelles } = options;

  const source = new VectorSource({ features: [] as any[] });

  const layer = new VectorLayer({
    source,
    visible,
    properties: { name: 'transactions_fao' },
    style: (feature) => {
      const isSelected = feature.get("name") === selectedParcelle;
      const svb = feature.get("svb");
      
      if (isSelected) {
        return svb === "green" ? transactionsFaoStyles.greenSelected : transactionsFaoStyles.redSelected;
      } else {
        return svb === "green" ? transactionsFaoStyles.green : transactionsFaoStyles.red;
      }
    },
    zIndex: 10,
  });

  // Function to update data from existing parcelles data
  const updateFromParcelles = (parcellesData: any[]) => {
    const isVisible = layer.getVisible();
    // console.log('Transactions FAO layer: updateFromParcelles called, visible:', isVisible, 'parcelles:', parcellesData.length);
    
    if (!isVisible) return;
    
    // Clear existing features
    source.clear();
    
    // Add features for transactions from parcelles data
    parcellesData.forEach((transaction: any) => {
      if (transaction.position_sur_map?.coordinates) {
        const [lon, lat] = transaction.position_sur_map.coordinates;
        const feature = new Feature({
          type: "geoMarker",
          geometry: new Point(fromLonLat([lon, lat])),
          name: transaction.NoParcelle,
          address: `${transaction.Adresse}, ${transaction.NPA} ${transaction.CommuneAdresse}`,
          svb: transaction.SVB_Verifiee ? "green" : "red",
          dataType: "transactions_fao",
          transactionData: transaction,
        });
        source.addFeature(feature as any);
      }
    });
    
    // console.log('Transactions FAO layer: added', source.getFeatures().length, 'features');
  };

  // Function to refresh data based on map extent and filters (fallback)
  const refreshData = async (mapInstance: any, currentFilters?: any, parcellesData?: any[]) => {
    const isVisible = layer.getVisible();
    // console.log('Transactions FAO layer: refreshData called, visible:', isVisible);
    
    // If we have parcelles data, use it directly
    if (parcellesData && parcellesData.length > 0) {
      updateFromParcelles(parcellesData);
      return parcellesData;
    }
    
    try {
      if (!isVisible) return;
      
      const view = mapInstance.getView();
      const extent = view.calculateExtent(mapInstance.getSize());
      const [minX, minY, maxX, maxY] = extent;
      
      // Transform extent from Web Mercator to WGS84
      const [minLon, minLat] = transform([minX, minY], 'EPSG:3857', 'EPSG:4326');
      const [maxLon, maxLat] = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326');
      
      // Build API parameters like in the original MapComponent
      const apiParams = new URLSearchParams();
      
      // Add corner coordinates
      const corners = {
        bl: `${minLon},${minLat}`,
        br: `${maxLon},${minLat}`,
        tl: `${minLon},${maxLat}`,
        tr: `${maxLon},${maxLat}`
      };
      
      apiParams.append("bl", corners.bl);
      apiParams.append("br", corners.br);
      apiParams.append("tl", corners.tl);
      apiParams.append("tr", corners.tr);
      apiParams.append("limit", "2000");
      apiParams.append("offset", "0");
      // Encode filters properly to avoid URL encoding issues
      const filterString = JSON.stringify(currentFilters || {});
      apiParams.append("filter", filterString);

      // Fetch transactions data using the same API as the original
      const url = `https://melcarnedirectus2.digipulse.ch/search_by_location?${apiParams.toString()}`;
      // console.log('Transactions FAO layer: API URL:', url);
      const response = await axiosInstance.get(url);
      
      const transactions = response.data.filter((p: any) => !!p.position_sur_map);
      console.log('Transactions FAO layer: loaded', transactions.length, 'transactions');
      
      // Clear existing features
      source.clear();
      
      // Add features for transactions
      transactions.forEach((transaction: any) => {
        if (transaction.position_sur_map?.coordinates) {
          const [lon, lat] = transaction.position_sur_map.coordinates;
          const feature = new Feature({
            type: "geoMarker",
            geometry: new Point(fromLonLat([lon, lat])),
            name: transaction.NoParcelle,
            address: `${transaction.Adresse}, ${transaction.NPA} ${transaction.CommuneAdresse}`,
            svb: transaction.SVB_Verifiee ? "green" : "red",
            dataType: "transactions_fao",
            transactionData: transaction,
          });
          source.addFeature(feature as any);
        }
      });
      
      // Show the layer after loading data
      layer.setVisible(true);
      
      return transactions;
      
    } catch (error) {
      console.error('Error fetching transactions_fao data:', error);
      return [];
    }
  };

  // Attach functions to layer for external access
  (layer as any)._refreshData = refreshData;
  (layer as any)._updateFromParcelles = updateFromParcelles;

  return layer;
}
